import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

// ── 速度感金色拖尾 ────────────────────────────────────────────────
function CursorTrail() {
  const containerRef = useRef(null);
  const particles = useRef([]);
  const mouse = useRef({ x: -200, y: -200 });
  const prevMouse = useRef({ x: -200, y: -200 });
  const dotRef = useRef(null);
  const raf = useRef(null);
  const lastEmit = useRef(0);
  const POOL = 35;

  useEffect(() => {
    const container = containerRef.current;

    // 粒子池
    for (let i = 0; i < POOL; i++) {
      const el = document.createElement('div');
      el.style.cssText = `
        position:fixed; border-radius:50%; pointer-events:none;
        z-index:99996; background:#F5A623; opacity:0;
        will-change:transform,opacity; transform:translate(-50%,-50%);
      `;
      container.appendChild(el);
      particles.current.push({ el, x:0, y:0, vx:0, vy:0, opacity:0, size:0, life:0, maxLife:0, active:false });
    }
    let pIdx = 0;

    const emit = (x, y, speed, dx, dy) => {
      const now = performance.now();
      const interval = Math.max(8, 48 - speed * 1.8);
      if (now - lastEmit.current < interval) return;
      lastEmit.current = now;

      const burst = speed > 18 ? 3 : speed > 8 ? 2 : 1;
      for (let b = 0; b < burst; b++) {
        const p = particles.current[pIdx % POOL];
        pIdx++;
        const len = Math.sqrt(dx*dx + dy*dy) || 1;
        const nx = dx / len, ny = dy / len;
        const scatter = (Math.random() - 0.5) * 0.4;
        // 沿运动反方向喷出（形成尾巴）
        p.vx = (-nx + scatter) * (Math.random() * 1.8 + 0.4);
        p.vy = (-ny + scatter) * (Math.random() * 1.8 + 0.4);
        p.x = x;
        p.y = y;
        p.size = Math.min(speed * 0.2 + 1.5, 7);
        p.opacity = Math.min(speed * 0.03 + 0.15, 0.9);
        p.life = 0;
        p.maxLife = Math.max(10, Math.min(speed * 0.9 + 6, 38));
        p.active = true;
      }
    };

    const tick = () => {
      const dx = mouse.current.x - prevMouse.current.x;
      const dy = mouse.current.y - prevMouse.current.y;
      const speed = Math.sqrt(dx*dx + dy*dy);

      // 小金点
      if (dotRef.current) {
        dotRef.current.style.left = mouse.current.x + 'px';
        dotRef.current.style.top = mouse.current.y + 'px';
        dotRef.current.style.opacity = '1';
      }

      if (speed > 1.2) emit(mouse.current.x, mouse.current.y, speed, dx, dy);

      particles.current.forEach(p => {
        if (!p.active) return;
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.91;
        p.vy *= 0.91;
        const t = p.life / p.maxLife;
        const op = p.opacity * (1 - t);
        const sz = p.size * (1 - t * 0.5);
        if (op < 0.01) { p.active = false; p.el.style.opacity = '0'; return; }
        p.el.style.width = sz + 'px';
        p.el.style.height = sz + 'px';
        p.el.style.left = p.x + 'px';
        p.el.style.top = p.y + 'px';
        p.el.style.opacity = op;
      });

      prevMouse.current = { ...mouse.current };
      raf.current = requestAnimationFrame(tick);
    };

    const onMove = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', onMove);
    raf.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf.current);
      particles.current.forEach(p => p.el.remove());
      particles.current = [];
    };
  }, []);

  return (
    <>
      <div ref={containerRef} />
      <div ref={dotRef} style={{
        position: 'fixed', zIndex: 99999, pointerEvents: 'none',
        width: 8, height: 8, borderRadius: '50%',
        background: '#F5A623', transform: 'translate(-50%,-50%)',
        willChange: 'left,top', left: '-200px', top: '-200px',
      }} />
    </>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  const navScrolled = !isHome || scrolled;

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    padding: navScrolled ? '0.7rem 2.5rem' : '1.2rem 2.5rem',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    transition: 'all 0.3s ease',
    background: navScrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
    backdropFilter: navScrolled ? 'blur(10px)' : 'none',
    boxShadow: navScrolled ? '0 2px 20px rgba(26,43,95,0.12)' : 'none',
  };

  const linkStyle = {
    textDecoration: 'none', fontSize: '0.95rem', fontWeight: 500,
    color: navScrolled ? 'var(--navy)' : 'rgba(255,255,255,0.92)',
    transition: 'color 0.2s',
  };

  return (
    <>
      <CursorTrail />
      <style>{`
        @media (min-width: 900px) { .nav-desktop { display: block !important; } }
        *:not(.pac-container):not(.pac-item):not(.pac-item *):not(.pac-matched):not(input):not(textarea):not(select) { cursor: none !important; }
        input, textarea, select { cursor: text !important; caret-color: var(--navy); }
        /* 清除所有密码管理器/浏览器扩展注入的图标 */
        input { background-image: none !important; }
        input::-ms-reveal, input::-ms-clear { display: none !important; }
        input[type="text"]::-webkit-contacts-auto-fill-button,
        input[type="text"]::-webkit-credentials-auto-fill-button { visibility: hidden !important; display: none !important; pointer-events: none !important; }
        /* LastPass */
        input[data-lpignore], div[id^="__lpform_"] { display: none !important; }
        [data-lastpass-icon-root], [data-lastpass-root] { display: none !important; }
        /* 1Password */
        [data-1p-ignore] ~ * [data-1p-id] { display: none !important; }
        /* Bitwarden / generic injected overlays */
        input ~ [style*="position: absolute"][style*="z-index"],
        input + div[style*="position:absolute"] { display: none !important; }
        .pac-container { z-index: 10001 !important; font-family: 'DM Sans', sans-serif !important; border-radius: 10px !important; box-shadow: 0 8px 30px rgba(26,43,95,0.15) !important; margin-top: 4px; }
        .pac-item { padding: 0.5rem 1rem !important; font-size: 0.9rem !important; cursor: pointer !important; }
        .pac-item:hover { background: rgba(245,166,35,0.08) !important; }
        .pac-icon { display: none !important; }
        .pac-item-query { font-weight: 600; color: var(--navy); }
      `}</style>

      <nav style={navStyle}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', textDecoration: 'none' }}>
          <img src="/logo-top.png" alt="SolarSavings" style={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }} />
          <span style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.35rem', color: navScrolled ? 'var(--navy)' : '#fff', transition: 'color 0.3s', letterSpacing: '-0.01em' }}>SolarSavings</span>
        </Link>

        <ul style={{ display: 'flex', alignItems: 'center', gap: '1.75rem', listStyle: 'none', margin: 0, padding: 0 }}>
          {[['Home', '/'], ['Commercial', '/commercial'], ['Residential', '/residential']].map(([label, to]) => (
            <li key={to} style={{ display: 'none' }} className="nav-desktop">
              <Link to={to} style={{ ...linkStyle, color: location.pathname === to ? 'var(--gold)' : linkStyle.color }}>{label}</Link>
            </li>
          ))}
          <li style={{ display: 'none' }} className="nav-desktop">
            <a href={isHome ? '#brands' : '/#brands'} style={linkStyle}>Brands</a>
          </li>
          <li style={{ display: 'none' }} className="nav-desktop">
            <a href={isHome ? '#insights' : '/#insights'} style={linkStyle}>Solar Insights</a>
          </li>
          <li style={{ display: 'none' }} className="nav-desktop">
            <Link to="/contact" style={{ ...linkStyle, color: location.pathname === '/contact' ? 'var(--gold)' : linkStyle.color }}>Contact</Link>
          </li>
        </ul>

        <Link to="/residential"
          style={{ background: 'var(--gold)', color: 'var(--navy)', border: 'none', padding: '0.75rem 1.6rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none', whiteSpace: 'nowrap', display: 'inline-block' }}>
          Calculate My Savings →
        </Link>

        <button style={{ display: 'flex', flexDirection: 'column', gap: 5, background: 'none', border: 'none', padding: 4, marginLeft: '1rem' }}
          onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          {[0,1,2].map(i => <span key={i} style={{ display: 'block', width: 26, height: 2.5, background: navScrolled ? 'var(--navy)' : '#fff', borderRadius: 2, transition: 'all 0.3s' }} />)}
        </button>
      </nav>

      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--navy)', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2rem' }}>
          <button onClick={() => setMenuOpen(false)} style={{ position: 'absolute', top: '1.5rem', right: '2rem', fontSize: '2rem', color: '#fff', background: 'none', border: 'none' }}>✕</button>
          {[['Home', '/'], ['Commercial', '/commercial'], ['Residential', '/residential'], ['Contact', '/contact']].map(([label, to]) => (
            <Link key={to} to={to} style={{ color: '#fff', textDecoration: 'none', fontSize: '1.5rem', fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{label}</Link>
          ))}
          <Link to="/residential" style={{ background: 'var(--gold)', color: 'var(--navy)', padding: '0.875rem 2rem', borderRadius: 50, fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '1.1rem', textDecoration: 'none' }}>
            Calculate My Savings →
          </Link>
        </div>
      )}
    </>
  );
}
